import moment from 'moment';
import React from 'react';
import { Box, Spinner, Text } from '@solvhealth/jigsaw-v1';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { useLocationSlots } from './hooks/useSlots';
import NextAvailableBeyondSameDay from '~/components/CareTeam/NextAvailableBeyondSameDay';
import FeaturedSlots from '~/components/CareTeam/FeaturedSlots';
import { LocationWithCareTypes } from '~/components/CareTeam/hooks/useCareTeam';
import {
  filterSlotsByDay,
  removeSlotsBeforeDisableReservationsUntilTimestamp,
} from '~/core/location/slots';
import { SlotShape } from '~/shapes/Slot';
import associatedTelemedLocation from '~/ducks/associatedTelemedLocation';

type Props = {
  location: LocationWithCareTypes;
};

/**
 * Fetches and displays the either same day treatment or beyond same day treatment based upon the location's timezone.
 */
const NextAvailable = ({ location }: Props) => {
  const { t } = useTranslation(['account']);
  const { data, status, isLoading } = useLocationSlots(location.id);
  const todayMoment = moment().tz(location.timeZone);
  let locationSlotsToday: SlotShape[] =
    data && filterSlotsByDay(data?.data.slots, todayMoment.date(), location.timeZone);

  const { data: associatedTelemedLocation } = useLocationSlots(
    location.associatedTelemedLocationId
  );

  // Note: These associated slots will be sorted and displayed intermixed with in-person slots from another location.
  // To keep the timezone consistent, the in-person location's timezone is being used instead of the timezone this associated location.
  let associatedSlotsToday = !isEmpty(associatedTelemedLocation?.data.slots)
    ? filterSlotsByDay(associatedTelemedLocation?.data.slots, todayMoment.date(), location.timeZone)
    : [];

  locationSlotsToday = removeSlotsBeforeDisableReservationsUntilTimestamp(
    locationSlotsToday,
    location
  );
  associatedSlotsToday = removeSlotsBeforeDisableReservationsUntilTimestamp(
    associatedSlotsToday,
    associatedTelemedLocation
  );
  let allSlotsToday: SlotShape[] =
    status === 'success' && data
      ? locationSlotsToday
          .concat(associatedSlotsToday ?? [])
          .sort((a, b) => +new Date(a.appointmentDate) - +new Date(b.appointmentDate))
          .filter((slot) => slot.availability > 0 && !slot.isReservationsDisabled)
      : [];

  return (
    <Box>
      {isLoading && (
        <Box alignItems="center" as="div" display="flex" justifyContent="center">
          <Spinner color="crunchberry-light" size="xs" />{' '}
          <Text as="span" ml={0.5} textColor="crunchberry">
            {t('account:availability.loading')}
          </Text>
        </Box>
      )}
      {!isEmpty(allSlotsToday) ? (
        <FeaturedSlots location={location} slotsData={allSlotsToday} />
      ) : (
        <NextAvailableBeyondSameDay location={location} />
      )}
    </Box>
  );
};

export default NextAvailable;
